import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Check, ChevronLeft, FileText, X } from "lucide-react";
import { callApi } from "../api/api";
import { useAccess } from "../context/AccessContext";
import { useReviewMode } from "../context/ReviewModeContext";
import { queueDestinationOptions } from "../context/WorkspaceContext";
import { resolveAdlsPath } from "../utils/resolveAdlsPath";

const LINE_TONES = {
  DWF: { background: "#dbeafe", color: "#1d4ed8" },
  COM: { background: "#ede9fe", color: "#6d28d9" },
  CEA: { background: "#dcfce7", color: "#15803d" },
};

const STATUS_STYLES = {
  "In Progress": { background: "#eff6ff", color: "#2563eb", border: "1px solid #bfdbfe" },
  Unassigned: { background: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" },
  Returned: { background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" },
  Referred: { background: "#fff7ed", color: "#c2410c", border: "1px solid #fed7aa" },
  Completed: { background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" },
};

const EVENT_COLOR = {
  ROUTED: "#2563eb",
  REROUTED: "#2563eb",
  ASSIGNED: "#2563eb",
  REASSIGNED: "#7c3aed",
  RETURNED: "#c2410c",
  COMPLETED: "#16a34a",
  NOTE: "#64748b",
};

const CHIP_STYLES = {
  Note: { background: "#f1f5f9", color: "#334155" },
  Refer: { background: "#fff7ed", color: "#c2410c" },
  Return: { background: "#fef2f2", color: "#dc2626" },
  Route: { background: "#eff6ff", color: "#2563eb" },
  Complete: { background: "#f0fdf4", color: "#16a34a" },
  Assign: { background: "#f5f3ff", color: "#6d28d9" },
};

function lineLabel(lob, options) {
  const value = String(lob || "").trim();
  if (!value) return "";
  const match = options.find((item) => (
    item.code.toLowerCase() === value.toLowerCase()
    || item.name.toLowerCase() === value.toLowerCase()
  ));
  return match?.name || value;
}

function formatWhen(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}

function statusLabel(status) {
  if (!status) return "In Progress";
  return String(status)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function activityChip(event) {
  if (event.event_type === "NOTE") return "Note";
  if (event.event_type === "RETURNED") {
    return event.to_destination_team_code === "OPERATION" ? "Return" : "Refer";
  }
  if (event.event_type === "ROUTED" || event.event_type === "REROUTED") return "Route";
  if (event.event_type === "COMPLETED") return "Complete";
  if (event.event_type === "ASSIGNED" || event.event_type === "REASSIGNED") return "Assign";
  return statusLabel(event.event_type);
}

function activityTitle(event) {
  const team = String(event.to_destination_team_code || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  if (event.event_type === "ROUTED" || event.event_type === "REROUTED") {
    return team ? `Routed to ${team}` : "Routed";
  }
  if (event.event_type === "ASSIGNED") return "Assigned";
  if (event.event_type === "REASSIGNED") return "Reassigned";
  if (event.event_type === "RETURNED") {
    return event.to_destination_team_code === "OPERATION"
      ? "Returned to Operations"
      : "Referred";
  }
  if (event.event_type === "COMPLETED") return "Completed";
  if (event.event_type === "NOTE") return "";
  return statusLabel(event.event_type);
}

async function loadFilePreview(instance, accounts, path) {
  const filesystemDefault = process.env.REACT_APP_ADLS_SEARCH_FILESYSTEM || "cfpuw";
  const { filesystem, path: filePath } = resolveAdlsPath(path, filesystemDefault);
  const lower = filePath.toLowerCase();
  const needsBlob = /\.(tif|tiff|eml|heic|heif)$/.test(lower);
  const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  if (needsBlob) {
    const token = await instance.acquireTokenSilent({
      account: instance.getActiveAccount() || accounts?.[0],
      scopes: [`api://${process.env.REACT_APP_BACKEND_CLIENT_ID}/access_as_user`],
    });
    const view = lower.endsWith(".eml") ? "&view=body" : "";
    const response = await fetch(
      `${baseUrl}/api/adls/preview?filesystem=${encodeURIComponent(filesystem)}&path=${encodeURIComponent(filePath)}${view}`,
      { headers: { Authorization: `Bearer ${token.accessToken}` } },
    );
    if (!response.ok) throw new Error(`Failed to preview file (${response.status})`);
    if (lower.endsWith(".eml")) {
      const email = await response.json();
      return { kind: "html", html: email.html || "" };
    }
    const blob = await response.blob();
    return { kind: "blob", url: URL.createObjectURL(blob) };
  }

  const response = await callApi(instance, accounts, "GET", "/api/adls/preview-url", null, {
    params: { filesystem, path: filePath },
  });
  if (!response?.data?.url) throw new Error("Preview URL missing");
  return { kind: "url", url: response.data.url };
}

export default function UnderwritingWorkAction({ row, onBack }) {
  const { instance, accounts } = useMsal();
  const { departments } = useAccess();
  const routeOptions = useMemo(
    () => queueDestinationOptions(departments, ["UNDERWRITING"]),
    [departments],
  );
  const { setReviewMode } = useReviewMode();
  const [activity, setActivity] = useState([]);
  const [activityError, setActivityError] = useState("");
  const [stage, setStage] = useState("active");
  const [dialog, setDialog] = useState(null);
  const [actionError, setActionError] = useState("");
  const [savingAction, setSavingAction] = useState(false);
  const [activeFile, setActiveFile] = useState(0);
  const [emailHtml, setEmailHtml] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [lineOptions, setLineOptions] = useState([]);
  const files = row.attachments || [];
  const emailFile = files.find((file) => file.type === "email") || null;
  const selected = files[activeFile] || emailFile || null;
  const status = statusLabel(row.queue_status);
  const policy = row.policy_number || "N/A";
  const escalated = String(row.queue_status || "").toUpperCase() === "RETURNED";
  const badgeLabel = escalated ? "Referred" : status;
  const escalatedBy = escalated
    ? activity.find((event) => (
      event.event_type === "RETURNED" && event.to_destination_team_code !== "OPERATION"
    ))?.actor || ""
    : "";
  const businessLine = lineLabel(row.lob, lineOptions);
  const referralReason = escalated ? String(row.return_reason || "").trim() : "";

  const loadHistory = async () => {
    const response = await callApi(instance, accounts, "GET", `/api/team-queue/history/${row.msg_id}`);
    const events = response?.data?.data || [];
    setActivity([...events].reverse());
    setActivityError("");
  };

  useEffect(() => {
    if (!accounts.length) return undefined;
    let cancelled = false;
    callApi(instance, accounts, "GET", "/api/admin/lookup/line_of_business")
      .then((response) => {
        if (cancelled) return;
        const options = (response?.data?.data || [])
          .map((item) => ({
            code: String(item.lookup_code || "").trim(),
            name: String(item.lookup_name || item.lookup_code || "").trim(),
          }))
          .filter((item) => item.code);
        setLineOptions(options);
      })
      .catch(() => {
        if (!cancelled) setLineOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [accounts, instance]);

  useEffect(() => {
    let cancelled = false;
    callApi(instance, accounts, "GET", `/api/team-queue/history/${row.msg_id}`)
      .then((response) => {
        if (cancelled) return;
        const events = response?.data?.data || [];
        setActivity([...events].reverse());
      })
      .catch((error) => {
        if (!cancelled) setActivityError(error.message || "Failed to load activity");
      });
    return () => {
      cancelled = true;
    };
  }, [accounts, instance, row.msg_id]);

  const saveNote = async () => {
    const text = noteDraft.trim();
    if (!text) return;
    setSavingNote(true);
    setActionError("");
    try {
      await callApi(instance, accounts, "POST", "/api/team-queue/work-action", {
        action: "note",
        msg_id: row.msg_id,
        reason: text,
      });
      setNoteDraft("");
      await loadHistory();
    } catch (saveError) {
      setActionError(saveError.message || "Failed to save the note");
    } finally {
      setSavingNote(false);
    }
  };

  useLayoutEffect(() => {
    setReviewMode(true);
    return () => setReviewMode(false);
  }, [setReviewMode]);

  useEffect(() => {
    if (!emailFile?.path) return undefined;
    let cancelled = false;
    loadFilePreview(instance, accounts, emailFile.path)
      .then((preview) => {
        if (!cancelled && preview.kind === "html") setEmailHtml(preview.html);
      })
      .catch((loadError) => {
        if (!cancelled) setPreviewError(loadError.message || "Failed to load the message");
      });
    return () => {
      cancelled = true;
    };
  }, [accounts, emailFile?.path, instance]);

  useEffect(() => {
    if (!selected?.path || selected.type === "email") {
      setPreviewUrl("");
      return undefined;
    }
    let cancelled = false;
    let blobUrl = "";
    loadFilePreview(instance, accounts, selected.path)
      .then((preview) => {
        if (cancelled) {
          if (preview.url) URL.revokeObjectURL(preview.url);
          return;
        }
        blobUrl = preview.url || "";
        setPreviewUrl(blobUrl);
      })
      .catch((loadError) => {
        if (!cancelled) setPreviewError(loadError.message || "Failed to load the file");
      });
    return () => {
      cancelled = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [accounts, instance, selected?.path, selected?.type]);

  const saveAction = async (action, extra = {}) => {
    setSavingAction(true);
    setActionError("");
    try {
      await callApi(instance, accounts, "POST", "/api/team-queue/work-action", {
        action,
        msg_id: row.msg_id,
        ...extra,
      });
      if (action === "complete") {
        setStage("complete");
        return;
      }
      onBack();
    } catch (saveError) {
      setActionError(saveError.message || "Failed to update the work item");
      setDialog(null);
    } finally {
      setSavingAction(false);
    }
  };

  if (stage === "complete") {
    return (
      <div style={styles.doneWrap}>
        <div style={styles.doneIcon}>
          <Check size={28} color="#16a34a" />
        </div>
        <div style={styles.doneTitle}>Work Completed</div>
        <div style={styles.doneText}>
          {policy} is stored as completed on the underwriting queue.
        </div>
        <button type="button" style={styles.blueButton} onClick={onBack}>
          Back to Queue
        </button>
      </div>
    );
  }

  const showingEmail = !selected || selected.type === "email";

  return (
    <div style={styles.workspace}>
      <section style={styles.reviewPane}>
        <div style={styles.viewerHeader}>
          <button type="button" style={styles.back} onClick={onBack}>
            <ChevronLeft size={16} />
            Back to Queue
          </button>
          <div style={styles.mailText}>
            <div style={styles.mailSubject}>{row.email_subject || "—"}</div>
            <div style={styles.mailFrom}>
              From: {row.mail_from_address || "—"} · {formatWhen(row.received_datetime)}
            </div>
          </div>
        </div>
        <div style={styles.fileStrip}>
          {files.map((file, index) => (
            <button
              key={`${file.type}-${file.path}`}
              type="button"
              style={{
                ...styles.fileButton,
                background: index === activeFile ? "#2563eb" : "#fff",
                color: index === activeFile ? "#fff" : "#0f172a",
                border: index === activeFile ? "1px solid #2563eb" : "1px solid #e2e8f0",
              }}
              onClick={() => setActiveFile(index)}
            >
              <FileText size={14} color={index === activeFile ? "#fff" : "#64748b"} />
              <span style={styles.fileName}>{file.name || "File"}</span>
            </button>
          ))}
        </div>
        <div style={styles.viewerBody}>
          {previewError && <p style={styles.notes}>{previewError}</p>}
          {showingEmail && emailHtml && (
            <iframe title="Email" sandbox="" srcDoc={emailHtml} style={styles.frame} />
          )}
          {showingEmail && !emailHtml && !previewError && (
            <p style={styles.notes}>Loading message...</p>
          )}
          {!showingEmail && previewUrl && (
            <iframe title={selected?.name || "Attachment"} src={previewUrl} style={styles.frame} />
          )}
        </div>
      </section>

      <aside style={styles.toolbarPane}>
        <div style={styles.toolbarHead}>
          <div style={styles.policy}>{policy}</div>
          <div style={styles.headBadges}>
            {businessLine && (
              <span style={{ ...styles.badge, ...(LINE_TONES[businessLine.toUpperCase()] || styles.lineFallback) }}>
                {businessLine}
              </span>
            )}
            <span style={{ ...styles.badge, ...(STATUS_STYLES[badgeLabel] || STATUS_STYLES["In Progress"]) }}>
              {badgeLabel}
            </span>
          </div>
        </div>
        {escalatedBy && (
          <div style={styles.returnedBy}>Referred by {escalatedBy}</div>
        )}
        {referralReason && (
          <div style={styles.referralBox}>
            <div style={styles.referralLabel}>Referral reason</div>
            <div style={styles.referralText}>{referralReason}</div>
          </div>
        )}
        <div style={styles.actions}>
          <button
            type="button"
            style={styles.completeButton}
            disabled={savingAction}
            onClick={() => saveAction("complete")}
          >
            Complete
          </button>
          <button type="button" style={styles.escalateButton} onClick={() => setDialog("escalate")}>
            Refer
          </button>
          <button type="button" style={styles.returnButton} onClick={() => setDialog("return")}>
            Return to Operations
          </button>
          <button type="button" style={styles.routeButton} onClick={() => setDialog("route")}>
            Route
          </button>
        </div>
        {actionError && <div style={styles.actionError}>{actionError}</div>}
        <div style={styles.activityScroll}>
          <section style={styles.notesSection}>
            <textarea
              rows={3}
              value={noteDraft}
              onChange={(event) => setNoteDraft(event.target.value)}
              placeholder="Add a note"
              style={styles.noteInput}
            />
            <div style={styles.notesFoot}>
              <button
                type="button"
                style={{
                  ...styles.noteSave,
                  ...(noteDraft.trim() && !savingNote ? styles.noteSaveReady : null),
                }}
                disabled={!noteDraft.trim() || savingNote}
                onClick={saveNote}
              >
                Save
              </button>
            </div>
          </section>
          <h2 style={styles.cardTitle}>Activity</h2>
          {activityError && <p style={styles.notes}>{activityError}</p>}
          {!activityError && activity.length === 0 && (
            <p style={styles.notes}>No activity recorded yet.</p>
          )}
          {activity.map((event, index) => {
            const chip = activityChip(event);
            const title = activityTitle(event);
            return (
              <div key={event.team_queue_log_id} style={styles.event}>
                <div style={styles.rail}>
                  <span style={{ ...styles.dot, background: EVENT_COLOR[event.event_type] || "#94a3b8" }} />
                  {index < activity.length - 1 && <span style={styles.line} />}
                </div>
                <div style={styles.eventBody}>
                  <div style={styles.eventHeading}>
                    <span style={{ ...styles.chip, ...(CHIP_STYLES[chip] || CHIP_STYLES.Note) }}>
                      {chip}
                    </span>
                    {title && <span style={styles.eventTitle}>{title}</span>}
                  </div>
                  <div style={styles.eventMeta}>
                    {event.actor || "—"} · {formatWhen(event.created_at)}
                  </div>
                  {event.comment && !/^Business area:/i.test(event.comment) && (
                    <div style={styles.comment}>{event.comment}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </aside>

      {dialog === "escalate" && (
        <EscalateDialog
          saving={savingAction}
          onClose={() => setDialog(null)}
          onConfirm={(reason) => saveAction("escalate", { reason })}
        />
      )}
      {dialog === "return" && (
        <ReturnDialog
          saving={savingAction}
          onClose={() => setDialog(null)}
          onConfirm={(reason) => saveAction("return", { reason })}
        />
      )}
      {dialog === "route" && (
        <RouteDialog
          options={routeOptions}
          saving={savingAction}
          onClose={() => setDialog(null)}
          onConfirm={(destination) => saveAction("route", { destination })}
        />
      )}
    </div>
  );
}

function EscalateDialog({ saving, onClose, onConfirm }) {
  const [reason, setReason] = useState("");

  return (
    <DialogShell title="Refer Work Item" onClose={onClose}>
      <p style={styles.dialogText}>
        This item returns to the underwriting queue on the Returned tab.
      </p>
      <label style={styles.fieldLabel}>Reason <span style={styles.required}>*</span></label>
      <textarea
        rows={4}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Describe the reason for the referral..."
        style={styles.textarea}
      />
      <div style={styles.dialogActions}>
        <button type="button" style={styles.ghostButton} onClick={onClose}>Cancel</button>
        <button
          type="button"
          style={{ ...styles.dangerButton, opacity: reason.trim() && !saving ? 1 : 0.4 }}
          disabled={!reason.trim() || saving}
          onClick={() => {
            const reasonText = reason.trim();
            onConfirm(reasonText);
          }}
        >
          Refer
        </button>
      </div>
    </DialogShell>
  );
}

function ReturnDialog({ saving, onClose, onConfirm }) {
  const [reason, setReason] = useState("");

  return (
    <DialogShell title="Return to Operations" onClose={onClose}>
      <p style={styles.dialogText}>
        The document returns to the operations queue on the Returned tab.
      </p>
      <label style={styles.fieldLabel}>Reason / Comment <span style={styles.required}>*</span></label>
      <textarea
        rows={5}
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        placeholder="Describe why this document is being returned to Operations..."
        style={styles.textarea}
      />
      <div style={styles.dialogActions}>
        <button type="button" style={styles.ghostButton} onClick={onClose}>Cancel</button>
        <button
          type="button"
          style={{ ...styles.returnConfirm, opacity: reason.trim() && !saving ? 1 : 0.4 }}
          disabled={!reason.trim() || saving}
          onClick={() => onConfirm(reason.trim())}
        >
          Return to Operations
        </button>
      </div>
    </DialogShell>
  );
}
function RouteDialog({ options, saving, onClose, onConfirm }) {
  const [destination, setDestination] = useState("");

  useEffect(() => {
    setDestination((current) => {
      if (current && options.some((option) => option.value === current)) return current;
      return options.find((option) => option.value === "CLAIMS")?.value || options[0]?.value || "";
    });
  }, [options]);

  return (
    <DialogShell title="Route to a team" onClose={onClose}>
      <p style={styles.dialogText}>
        The item leaves underwriting and arrives in the selected team queue.
      </p>
      <label style={styles.fieldLabel}>Destination team</label>
      <select value={destination} onChange={(event) => setDestination(event.target.value)} style={styles.input}>
        {options.filter((option) => option.value === "OPERATION" || option.value === "CLAIMS").map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
      <div style={styles.dialogActions}>
        <button type="button" style={styles.ghostButton} onClick={onClose}>Cancel</button>
        <button
          type="button"
          style={{ ...styles.blueButton, flex: 1, marginTop: 0, opacity: destination && !saving ? 1 : 0.4 }}
          disabled={!destination || saving}
          onClick={() => onConfirm(destination)}
        >
          Route
        </button>
      </div>
    </DialogShell>
  );
}
function DialogShell({ title, children, onClose }) {
  return (
    <div style={styles.backdrop}>
      <div style={styles.backdropScrim} />
      <div style={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="uw-action-dialog-title">
        <div style={styles.dialogHeader}>
          <h2 id="uw-action-dialog-title" style={styles.dialogTitle}>{title}</h2>
          <button type="button" style={styles.dialogClose} aria-label="Close" onClick={onClose}>
            <X size={16} strokeWidth={2} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}


const styles = {
  workspace: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    background: "#fff",
    overflow: "hidden",
  },
  reviewPane: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    background: "#fff",
  },
  toolbarPane: {
    width: 320,
    flexShrink: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    borderLeft: "1px solid #e2e8f0",
    background: "#fff",
    padding: 16,
    boxSizing: "border-box",
  },
  toolbarHead: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 },
  headBadges: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0 },
  lineFallback: { background: "#f1f5f9", color: "#334155" },
  returnedBy: { marginTop: 8, fontSize: 13, color: "#0f172a" },
  referralBox: {
    marginTop: 10,
    background: "#fff4e8",
    borderRadius: 8,
    padding: "8px 10px",
  },
  referralLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#c2410c",
  },
  referralText: {
    marginTop: 2,
    fontSize: 13,
    fontWeight: 400,
    color: "#0f172a",
    lineHeight: "18px",
  },
  activityScroll: { flex: 1, minHeight: 0, overflowY: "auto", marginTop: 16 },
  notesSection: { marginBottom: 20 },
  noteInput: {
    width: "100%",
    boxSizing: "border-box",
    marginTop: 12,
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    color: "#0f172a",
    resize: "vertical",
    fontFamily: "inherit",
  },
  notesFoot: { display: "flex", alignItems: "center", justifyContent: "flex-end", marginTop: 8 },
  noteSave: {
    border: "none",
    background: "#e2e8f0",
    color: "#94a3b8",
    borderRadius: 6,
    padding: "6px 14px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "default",
  },
  noteSaveReady: { background: "#2563eb", color: "#fff", cursor: "pointer" },
  fileStrip: {
    display: "flex",
    gap: 8,
    padding: "8px 16px",
    overflowX: "auto",
    flexShrink: 0,
    borderBottom: "1px solid #e2e8f0",
  },
  back: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    border: "none",
    background: "transparent",
    color: "#64748b",
    fontSize: 14,
    cursor: "pointer",
    padding: 0,
    flexShrink: 0,
  },
  card: {
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 16,
  },
  headerRow: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" },
  identity: { display: "flex", alignItems: "center", gap: 12 },
  docIcon: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "#eff6ff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "0 0 40px",
  },
  policy: { fontSize: 16, fontWeight: 700, color: "#0f172a" },
  assignment: { marginTop: 2, fontSize: 12, color: "#64748b" },
  assignee: { color: "#0f172a", fontWeight: 600 },
  badge: { borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600, whiteSpace: "nowrap" },
  actions: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    marginTop: 16,
  },
  completeButton: {
    border: "none",
    background: "#16a34a",
    color: "#fff",
    borderRadius: 6,
    padding: "8px 20px",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
  },
  escalateButton: {
    border: "1px solid #fecaca",
    background: "#fef2f2",
    color: "#dc2626",
    borderRadius: 6,
    padding: "8px 20px",
    fontSize: 14,
    cursor: "pointer",
  },
  returnButton: {
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#475569",
    borderRadius: 6,
    padding: "8px 20px",
    fontSize: 14,
    cursor: "pointer",
  },
  routeButton: {
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    color: "#2563eb",
    borderRadius: 6,
    padding: "8px 20px",
    fontSize: 14,
    cursor: "pointer",
  },
  actionError: { marginTop: 12, color: "#dc2626", fontSize: 13 },
  columns: { display: "grid", gap: 20, marginTop: 20 },
  stack: { display: "flex", flexDirection: "column", gap: 20 },
  cardTitle: {
    margin: "0 0 12px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#64748b",
  },
  detail: { display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 8 },
  detailLabel: { fontSize: 12, color: "#64748b" },
  detailValue: { fontSize: 12, fontWeight: 600, color: "#0f172a", textAlign: "right" },
  mailHeader: { display: "flex", justifyContent: "space-between", gap: 12, padding: "12px 16px", borderBottom: "1px solid #f1f5f9" },
  mailIdentity: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
  mailText: { minWidth: 0 },
  mailSubject: { fontSize: 12, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  mailFrom: { fontSize: 11, color: "#94a3b8" },
  mailActions: { display: "flex", alignItems: "center", gap: 8, flexShrink: 0 },
  fileCount: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "#f1f5f9",
    color: "#64748b",
    borderRadius: 4,
    padding: "2px 8px",
    fontSize: 11,
  },
  reviewButton: {
    border: "1px solid #bfdbfe",
    background: "#eff6ff",
    color: "#2563eb",
    borderRadius: 4,
    padding: "4px 10px",
    fontSize: 11,
    fontWeight: 600,
    cursor: "pointer",
  },
  snippet: {
    margin: 0,
    padding: "10px 16px 14px",
    fontSize: 12,
    lineHeight: "18px",
    color: "#94a3b8",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  notes: { margin: 0, fontSize: 13, color: "#64748b", fontStyle: "italic" },
  event: { display: "flex", gap: 12 },
  rail: { display: "flex", flexDirection: "column", alignItems: "center", width: 12, flexShrink: 0 },
  dot: { width: 8, height: 8, borderRadius: "50%", marginTop: 4 },
  line: { width: 1, flex: 1, background: "#e2e8f0", minHeight: 20, margin: "4px 0" },
  eventBody: { paddingBottom: 16, minWidth: 0 },
  eventHeading: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "1px 7px",
    fontSize: 10,
    fontWeight: 600,
    lineHeight: "14px",
  },
  eventTitle: { fontSize: 12, fontWeight: 600, color: "#0f172a" },
  eventMeta: { marginTop: 2, fontSize: 11, color: "#94a3b8" },
  comment: {
    marginTop: 6,
    fontSize: 11,
    color: "#64748b",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 4,
    padding: "4px 8px",
  },
  fixedIcon: { width: 16, height: 16, minWidth: 16, flex: "0 0 16px" },
  doneWrap: { height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", gap: 8 },
  doneIcon: { width: 64, height: 64, borderRadius: "50%", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center" },
  doneTitle: { fontSize: 18, fontWeight: 700, color: "#0f172a" },
  doneText: { fontSize: 14, color: "#64748b", maxWidth: 420 },
  blueButton: { marginTop: 8, border: "none", background: "#2563eb", color: "#fff", borderRadius: 6, padding: "8px 20px", cursor: "pointer" },
  backdrop: { position: "fixed", inset: 0, zIndex: 400, display: "flex", alignItems: "center", justifyContent: "center" },
  backdropScrim: { position: "absolute", inset: 0, background: "rgba(15, 23, 42, 0.3)" },
  dialog: { position: "relative", width: 420, maxWidth: "95vw", background: "#fff", borderRadius: 12, padding: 24, boxShadow: "0 20px 40px rgba(15, 23, 42, 0.18)" },
  dialogHeader: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 4 },
  dialogTitle: { margin: 0, fontSize: 16, color: "#0f172a" },
  dialogClose: { border: "none", background: "transparent", color: "#64748b", cursor: "pointer", padding: 0, lineHeight: 0, flexShrink: 0 },
  dialogText: { margin: "0 0 16px", fontSize: 12, color: "#64748b" },
  fieldLabel: { display: "block", margin: "12px 0 6px", fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" },
  required: { color: "#dc2626" },
  input: { width: "100%", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", fontSize: 14 },
  textarea: { width: "100%", border: "1px solid #e2e8f0", borderRadius: 6, padding: "8px 10px", fontSize: 14, resize: "none" },
  dialogActions: { display: "flex", gap: 8, marginTop: 20 },
  ghostButton: { flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", borderRadius: 6, padding: "8px 12px", cursor: "pointer" },
  dangerButton: { flex: 1, border: "none", background: "#dc2626", color: "#fff", borderRadius: 6, padding: "8px 12px", cursor: "pointer" },
  returnConfirm: { flex: 1, border: "none", background: "#c2410c", color: "#fff", borderRadius: 6, padding: "8px 12px", cursor: "pointer" },
  dialogDone: { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 8, padding: "8px 0" },
  roundIcon: { width: 48, height: 48, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center" },
  viewer: { position: "fixed", inset: 0, zIndex: 50, background: "rgba(15, 23, 42, 0.8)", display: "flex" },
  viewerMain: { flex: 1, minWidth: 0, background: "#fff", display: "flex", flexDirection: "column" },
  viewerHeader: { display: "flex", alignItems: "center", gap: 16, padding: "12px 16px", borderBottom: "1px solid #e2e8f0", flexShrink: 0 },
  viewerBody: { flex: 1, minHeight: 0 },
  viewerSide: { width: 320, flexShrink: 0, background: "#f8fafc", borderLeft: "1px solid #e2e8f0", display: "flex", flexDirection: "column" },
  sideTitle: { padding: "14px 16px", borderBottom: "1px solid #e2e8f0", fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "#64748b" },
  fileList: { padding: 12, display: "flex", flexDirection: "column", gap: 6, borderBottom: "1px solid #e2e8f0" },
  fileButton: { display: "flex", alignItems: "center", gap: 8, textAlign: "left", borderRadius: 8, padding: "8px 10px", cursor: "pointer", flex: "0 0 auto", maxWidth: 220 },
  fileName: { fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  previewPane: { flex: 1, minHeight: 0, padding: 12 },
  previewEmpty: { height: "100%", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 12, textAlign: "center", padding: 16 },
  frame: { width: "100%", height: "100%", border: "none", background: "#fff" },
};
